import { Navigation } from 'lucide-react';
import { useMapsPrefStore } from '../../store/mapsPrefStore';
import { buildDirectionsUrl, type DirectionsDestination } from '../../lib/directions';

interface DirectionsButtonProps {
  /** Where to navigate to — an Organization or a person's linked-org shape. */
  destination: DirectionsDestination;
  /** 'header' = full secondary button (action rows); 'inline' = compact link. */
  variant?: 'header' | 'inline';
  label?: string;
}

/**
 * One-tap "Directions" anchor. Reads the per-device maps-app preference,
 * builds the deep link, and renders nothing when there's no destination.
 * stopPropagation keeps it safe inside clickable rows/cards.
 */
export const DirectionsButton = ({
  destination,
  variant = 'header',
  label = 'Directions',
}: DirectionsButtonProps) => {
  const mapsApp = useMapsPrefStore((s) => s.mapsApp);
  const url = buildDirectionsUrl(destination, mapsApp);
  if (!url) return null;

  const common = {
    href: url,
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
  };

  if (variant === 'inline') {
    return (
      <a
        {...common}
        className="inline-flex items-center gap-1 text-sm font-medium text-blast-600 hover:text-blast-700 dark:text-blast-400 dark:hover:text-blast-400 hover:underline"
      >
        <Navigation className="h-4 w-4 flex-shrink-0" />
        {label}
      </a>
    );
  }

  return (
    <a
      {...common}
      className="btn-secondary text-sm justify-center flex-1 sm:flex-initial whitespace-nowrap"
    >
      <Navigation className="h-4 w-4 mr-1" />
      {label}
    </a>
  );
};
